/**
 * CALCULADORA 02 — NACE / AMPP SP0775
 * Taxa média de corrosão pela perda de massa de um CUPOM.
 *
 * Interface métrica: o usuário informa dimensões em mm, massa em g e as datas
 * de instalação e retirada. Área exposta (mm²), tempo (dias), densidade
 * (g/cm³) e perda de massa (g) são calculados pelo sistema.
 *
 * Calculadora independente: a entrada é o cupom, nunca leituras de ultrassom,
 * e o resultado nunca substitui automaticamente a taxa do API 570.
 */

/**
 * CR [mm/ano] = (3,65 × 10^5 × W) / (A × T × D)
 * com W em g, A em mm², T em dias e D em g/cm³.
 *
 * Origem do fator: W/(A×D) devolve penetração em cm³/mm² = 10³ mm; dividido
 * por T em dias dá mm/dia; × 365 dias/ano fecha em 3,65 × 10^5.
 */
export const NACE_CONSTANT_MM_PER_YEAR = 3.65e5;

/** Conversão exibida apenas na área de "outras unidades": 1 mm/ano = 39,3701 mpy. */
export const MPY_PER_MM_PER_YEAR = 39.3701;

export const NACE_DAYS_PER_YEAR = 365;

export interface CouponGeometry {
  lengthMm: number | null;
  widthMm: number | null;
  thicknessMm: number | null;
  /** Furo de fixação, quando houver. */
  holeDiameterMm?: number | null;
  /** Área protegida/mascarada (suporte, isolador), em mm². */
  maskedAreaMm2?: number | null;
}

export interface CouponAreaResult {
  /** Duas faces principais. */
  faceAreaMm2: number | null;
  /** Bordas (4 laterais). */
  edgeAreaMm2: number | null;
  /** Desconto das duas bocas do furo. */
  holeDeductionMm2: number;
  /** Parede interna do furo, que também fica exposta. */
  holeWallAreaMm2: number;
  maskedAreaMm2: number;
  exposedAreaMm2: number | null;
  warnings: string[];
}

export type AreaOrigin = "GEOMETRY" | "MANUAL";

export interface NaceCouponInputs {
  /** Material do cupom, só para registro. */
  couponMaterialId?: string | null;
  materialName?: string;
  /** g/cm³ — vem do catálogo de materiais de cupom. */
  densityGramsPerCm3: number | null;

  geometry: CouponGeometry;
  /** Ajuste técnico da área exposta; quando informado, prevalece sobre a geometria. */
  exposedAreaOverrideMm2?: number | null;
  areaOverrideJustification?: string;

  initialMassGrams: number | null;
  finalMassGrams: number | null;

  /** "yyyy-mm-dd" — o tempo de exposição sai daqui. */
  installationDate: string;
  removalDate: string;
  /** Só quando as datas não existirem. */
  exposureDaysOverride?: number | null;
}

export type NaceSeverity = "BAIXA" | "MODERADA" | "ALTA" | "SEVERA";

export interface NaceResult {
  massLossGrams: number | null;
  area: CouponAreaResult;
  exposedAreaMm2: number | null;
  areaOrigin: AreaOrigin;
  exposureDays: number | null;
  densityGramsPerCm3: number | null;
  corrosionRateMmPerYear: number | null;
  /** Secundário — exibido apenas em "outras unidades". */
  corrosionRateMpy: number | null;
  severity: NaceSeverity | null;
  warnings: string[];
}

const num = (value: number | null | undefined): number | null =>
  value !== null && value !== undefined && Number.isFinite(value) ? value : null;

/** Dias inteiros entre duas datas civis "yyyy-mm-dd" (sem deslocamento de fuso). */
export function daysBetween(fromDate: string, toDate: string): number | null {
  const parse = (value: string): number | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec((value ?? "").trim());
    if (!match) return null;
    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  };

  const from = parse(fromDate);
  const to = parse(toDate);
  if (from === null || to === null) return null;
  return Math.round((to - from) / (1000 * 60 * 60 * 24));
}

/**
 * Área exposta do cupom a partir da geometria real.
 *
 * Não assume "comprimento × largura": soma as duas faces e as quatro bordas,
 * desconta as bocas do furo, acrescenta a parede interna do furo (que também
 * corrói) e subtrai a área mascarada.
 */
export function calculateCouponArea(geometry: CouponGeometry): CouponAreaResult {
  const warnings: string[] = [];

  const length = num(geometry.lengthMm);
  const width = num(geometry.widthMm);
  const thickness = num(geometry.thicknessMm);
  const holeDiameter = num(geometry.holeDiameterMm) ?? 0;
  const masked = num(geometry.maskedAreaMm2) ?? 0;

  const missing: string[] = [];
  if (length === null || length <= 0) missing.push("comprimento");
  if (width === null || width <= 0) missing.push("largura");
  if (thickness === null || thickness <= 0) missing.push("espessura");

  if (missing.length > 0) {
    warnings.push(`Área não calculada: informe ${missing.join(", ")} do cupom (mm).`);
    return {
      faceAreaMm2: null,
      edgeAreaMm2: null,
      holeDeductionMm2: 0,
      holeWallAreaMm2: 0,
      maskedAreaMm2: masked,
      exposedAreaMm2: null,
      warnings
    };
  }

  const faceArea = 2 * length! * width!;
  const edgeArea = 2 * (length! * thickness!) + 2 * (width! * thickness!);

  const holeArea = holeDiameter > 0 ? (Math.PI * holeDiameter * holeDiameter) / 4 : 0;
  const holeDeduction = 2 * holeArea;
  const holeWallArea = holeDiameter > 0 ? Math.PI * holeDiameter * thickness! : 0;

  let exposed = faceArea + edgeArea - holeDeduction + holeWallArea - masked;

  if (holeDiameter > 0 && (holeDiameter >= width! || holeDiameter >= length!)) {
    warnings.push("Diâmetro do furo maior ou igual à dimensão do cupom: confira a geometria informada.");
  }
  if (exposed <= 0) {
    warnings.push("Área exposta resultante menor ou igual a zero: confira furo e área mascarada.");
    exposed = 0;
  }

  return {
    faceAreaMm2: faceArea,
    edgeAreaMm2: edgeArea,
    holeDeductionMm2: holeDeduction,
    holeWallAreaMm2: holeWallArea,
    maskedAreaMm2: masked,
    exposedAreaMm2: exposed > 0 ? exposed : null,
    warnings
  };
}

/**
 * Faixas de severidade da SP0775 para corrosão uniforme, em mm/ano.
 * Reproduzidas como publicadas — nenhuma faixa é inventada.
 */
export function classifyNaceSeverity(rateMmPerYear: number | null): NaceSeverity | null {
  if (rateMmPerYear === null || !Number.isFinite(rateMmPerYear)) return null;
  if (rateMmPerYear < 0.025) return "BAIXA";
  if (rateMmPerYear <= 0.125) return "MODERADA";
  if (rateMmPerYear <= 0.254) return "ALTA";
  return "SEVERA";
}

export function calculateNaceCoupon(inputs: NaceCouponInputs): NaceResult {
  const warnings: string[] = [];

  const initialMass = num(inputs.initialMassGrams);
  const finalMass = num(inputs.finalMassGrams);
  const massLoss = initialMass !== null && finalMass !== null ? initialMass - finalMass : null;

  if (massLoss === null) warnings.push("Informe a massa inicial e a massa final do cupom (g).");
  if (massLoss !== null && massLoss < 0) warnings.push("Massa final maior que a inicial: verifique a limpeza e a pesagem do cupom.");

  const area = calculateCouponArea(inputs.geometry);
  const override = num(inputs.exposedAreaOverrideMm2);

  let exposedArea: number | null;
  let areaOrigin: AreaOrigin;
  if (override !== null && override > 0) {
    exposedArea = override;
    areaOrigin = "MANUAL";
    if (!inputs.areaOverrideJustification?.trim()) {
      warnings.push("Área exposta ajustada manualmente sem justificativa técnica registrada.");
    }
  } else {
    exposedArea = area.exposedAreaMm2;
    areaOrigin = "GEOMETRY";
    warnings.push(...area.warnings);
  }

  const daysFromDates = daysBetween(inputs.installationDate, inputs.removalDate);
  const daysOverride = num(inputs.exposureDaysOverride);
  const exposureDays = daysFromDates !== null ? daysFromDates : daysOverride;

  if (exposureDays === null) {
    warnings.push("Informe a data de instalação e a data de retirada do cupom.");
  } else if (exposureDays <= 0) {
    warnings.push("Data de retirada anterior ou igual à de instalação: o tempo de exposição precisa ser positivo.");
  }

  const density = num(inputs.densityGramsPerCm3);
  if (density === null || density <= 0) {
    warnings.push("Densidade do material do cupom não definida: selecione o material do cupom.");
  }

  const canCompute =
    massLoss !== null &&
    exposedArea !== null && exposedArea > 0 &&
    exposureDays !== null && exposureDays > 0 &&
    density !== null && density > 0;

  const rateMmPerYear = canCompute
    ? (NACE_CONSTANT_MM_PER_YEAR * massLoss!) / (exposedArea! * exposureDays! * density!)
    : null;

  return {
    massLossGrams: massLoss,
    area,
    exposedAreaMm2: exposedArea,
    areaOrigin,
    exposureDays,
    densityGramsPerCm3: density,
    corrosionRateMmPerYear: rateMmPerYear,
    corrosionRateMpy: rateMmPerYear === null ? null : rateMmPerYear * MPY_PER_MM_PER_YEAR,
    severity: classifyNaceSeverity(rateMmPerYear),
    warnings
  };
}
